// src/pages/CustomerSignUpPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { signupSuccessPath } from '../utils/googleAuth';
import PasswordInput from '../components/PasswordInput';
import GoogleSignInButton, { GoogleAccountNote } from '../components/GoogleSignInButton';
import useGoogleAuthNavigation from '../hooks/useGoogleAuthNavigation';
import BrandName from '../components/BrandName';
import SpamFolderWarning from '../components/SpamFolderWarning';
import Seo from '../components/Seo';
import { SIMPLE_PAGES } from '../data/simplePages';
import { webPageJsonLd } from '../data/pageSchema';
import './AuthPages.css';

const seo = SIMPLE_PAGES.find((page) => page.path === '/customer-signup');

const CustomerSignUpPage = () => {
  const { signup, error, signInWithGoogle, completeGoogleSignup, pendingSetup, logout } = useAuth();
  const navigate          = useNavigate();
  useGoogleAuthNavigation('signup');
  const [form, setForm] = useState(() => ({
    name: pendingSetup?.name || '',
    email: pendingSetup?.email || '',
    password: '',
    location: '',
  }));
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = e => setForm({...form, [e.target.name]: e.target.value});

  useEffect(() => {
    if (!pendingSetup) return;
    setForm((prev) => ({
      ...prev,
      email: pendingSetup.email || prev.email,
      name: prev.name || pendingSetup.name || '',
    }));
  }, [pendingSetup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = pendingSetup
      ? await completeGoogleSignup('customer', form.name, { location: form.location })
      : await signup(form.email, form.password, 'customer', form.name, { location: form.location });
    if (result.success) {
      // If the verification email failed to send, go straight to /verify-email,
      // which explains the problem and offers Resend. A verified Google account
      // goes to the dashboard.
      const next = signupSuccessPath(result);
      if (next) navigate(next, { replace: true });
      else setSubmitted(true);
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    await signInWithGoogle();
    setGoogleLoading(false);
  };

  useEffect(() => {
    if (!submitted) return;
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate('/verify-email');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [submitted, navigate]);

  if (submitted) return (
    <div className="auth-page">
      <Seo title={seo.title} description={seo.description} path={seo.path} jsonLd={webPageJsonLd(seo)} />
      <div className="auth-card" style={{ textAlign: 'center' }}>
        <div className="auth-logo"><BrandName /></div>
        <SpamFolderWarning />
        <p className="auth-sub" style={{ marginTop: '4px' }}>
          Continuing to inbox instructions in <strong>{countdown}s</strong>…
        </p>
        <button
          className="btn btn-primary btn-full"
          style={{ marginTop: '12px' }}
          onClick={() => navigate('/verify-email')}
        >
          Continue Now
        </button>
      </div>
    </div>
  );

  return (
    <div className="auth-page">
      <Seo title={seo.title} description={seo.description} path={seo.path} jsonLd={webPageJsonLd(seo)} />
      <div className="auth-card">
        <div className="auth-logo"><BrandName /></div>
        <h2>Create Customer Account</h2>
        <p className="auth-sub">Start posting jobs and getting quotes today</p>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" name="name" placeholder="Your name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input className="form-input" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={handleChange} required readOnly={!!pendingSetup} />
          </div>
          {!pendingSetup && (
            <div className="form-group">
              <label className="form-label">Password *</label>
              <PasswordInput className="form-input" name="password" placeholder="Min 6 characters" value={form.password} onChange={handleChange} required autoComplete="new-password" />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Location</label>
            <input className="form-input" name="location" placeholder="e.g. Swansea, SA1" value={form.location} onChange={handleChange} />
          </div>
          <button type="submit" className="btn btn-primary btn-full" disabled={loading || googleLoading}>
            {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
          </button>
          {pendingSetup ? (
            <GoogleAccountNote email={pendingSetup.email} onUseDifferent={logout} />
          ) : (
            <GoogleSignInButton
              label="Sign up with Google"
              onClick={handleGoogle}
              disabled={loading}
              busy={googleLoading}
            />
          )}
        </form>
        <p className="auth-footer">Already have an account? <Link to="/login">Log in</Link></p>
      </div>
    </div>
  );
};

export default CustomerSignUpPage;
